import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionLabel } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { isNum } from '../shared/format';

/**
 * Module 1 — A1.7 "Other gaps" list.
 *
 * Ranks the roster + new-creator-health rate metrics by how far each sits below
 * its target (in percentage points), excluding the #1 already shown in the
 * Strategic Focus banner. Renders under the banner in the Focus view.
 *
 * Props:
 *   - data: manager_current_month row
 *   - excludeMetric: the biggest_gap_metric key (already surfaced in the banner)
 */
const RATE_METRICS = [
  { key: 'valid_go_live_rate', target: 30 },
  { key: 'rank_up_eligibility_rate_7d15h', target: 75 },
  { key: 'pk_penetration', target: 60 },
  { key: 't14_ratio', target: 40 },
  { key: 'go_live_rate_15d_45h_first30', target: 24 },
  { key: 'first_30d_pk_count_20_ratio', target: 13 },
];

const LABELS = {
  valid_go_live_rate: ['tcn.m1.metric.validGoLive', 'Valid go-LIVE rate'],
  rank_up_eligibility_rate_7d15h: ['tcn.m1.metric.rankUpEligibility', 'Rank-up eligibility (7D/15H)'],
  pk_penetration: ['tcn.m1.metric.pkPenetration', 'PK penetration'],
  t14_ratio: ['tcn.m1.metric.t14', 'T+14 ratio'],
  go_live_rate_15d_45h_first30: ['tcn.m1.metric.goLive15d45h', 'Go-LIVE 15d/45h (first 30)'],
  first_30d_pk_count_20_ratio: ['tcn.m1.metric.first30PK', 'First 30d PK ≥ 20'],
};

const OtherGapsCard = ({ data, excludeMetric }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const gaps = RATE_METRICS.map((m) => {
    const value = data[m.key];
    return { ...m, value, gap: isNum(value) ? value - m.target : null };
  })
    .filter((m) => m.key !== excludeMetric && isNum(m.gap) && m.gap < 0)
    .sort((a, b) => a.gap - b.gap);

  if (gaps.length === 0) return null;

  return (
    <Card>
      <SectionLabel>{t('tcn.m1.otherGaps.title', 'Other gaps')}</SectionLabel>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {gaps.map((m) => {
          const [k, fallback] = LABELS[m.key];
          return (
            <div
              key={m.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                borderRadius: 10,
                background: TCN.surface2,
                border: `1px solid ${TCN.border}`,
              }}
            >
              <span style={{ fontSize: 13, color: TCN.text }}>{t(k, fallback)}</span>
              <span
                dir="ltr"
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: TCN.amber,
                  fontVariantNumeric: 'tabular-nums',
                  fontFamily: TCN.fontMono,
                }}
              >
                {Math.round(m.gap)}
                {t('tcn.m1.otherGaps.pp', 'pp')}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default OtherGapsCard;
